import { Injectable } from '@angular/core';
import { UserService } from '../../auth/services/user.service';
import { StrategyPermissions } from '../../models/StrategyPermissions';
import { CustomerService } from '../../auth/services/customer.service';
import { IStrategyPermissions } from '../../models/IStrategyPermissions';
import { UserMgmtService } from '../../user-mgmt/user-mgmt.service';
import { IUser, Role } from '../../auth/models/IUser';

@Injectable()
export class StrategiesPermissionsService {
  static readonly ROLE_EDITOR = 'ROLE_EDITOR';
  static readonly ROLE_VIEWER = 'ROLE_VIEWER';
  static readonly ROLE_ADMIN = 'ROLE_ADMIN';

  private static readonly ROLE_VALUE_MAP = {
    ROLE_ADMIN: 3,
    ROLE_EDITOR: 2,
    ROLE_VIEWER: 1
  };
  private static readonly ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN';

  constructor(private userService: UserService,
    private customerService: CustomerService,
    private userMgmtService: UserMgmtService) { }

  set userInstitutionRole(userInstitutionRole) {
    localStorage.setItem('userInstitutionRole', userInstitutionRole);
  }

  set userInstitutionPermissions(userInstitutionPermissions: IStrategyPermissions) {
    localStorage.setItem('userInstitutionPermissions', JSON.stringify(userInstitutionPermissions));
  }

  get userInstitutionRole() {
    return localStorage.getItem('userInstitutionRole');
  }

  get userInstitutionPermissions(): IStrategyPermissions {
    const localStoragePerms = localStorage.getItem('userInstitutionPermissions');
    return JSON.parse(localStoragePerms);
  }

  // TODO move this to User model?
  static translateRole(string: string): string {
    switch (string) {
      case StrategiesPermissionsService.ROLE_ADMIN:
        return 'Admin';
      case StrategiesPermissionsService.ROLE_EDITOR:
        return 'Editor';
      case StrategiesPermissionsService.ROLE_VIEWER:
        return 'Viewer';
      default:
        return string;
    }

  }

  isUserSuperAdmin(): boolean {
    return this.userInstitutionRole === StrategiesPermissionsService.ROLE_SUPER_ADMIN;
  }

  isUserViewer(): boolean {
    return this.userInstitutionRole === StrategiesPermissionsService.ROLE_VIEWER;
  }

  populateSuperAdminRoleAndPermissions() {
    const user = this.userService.user;
    if (user.applicationRole && user.applicationRole.roleName === StrategiesPermissionsService.ROLE_SUPER_ADMIN) {
      this.userInstitutionPermissions = {
        canView: true, canAdd: true, canEditBody: true, canEditNameDates: true,
        canDelete: true, canDownload: true, canCopy: true, canManageUsers: true
      };
      this.userInstitutionRole = StrategiesPermissionsService.ROLE_SUPER_ADMIN;
    }
  }

  populateUserInstitutionRoleAndPermissions() {
    if (this.isUserSuperAdmin()) { return; }
    const user = this.userService.user;
    const storedAssociation = user.userAssociations ?
      user.userAssociations.find(uai => uai.institutionId === this.customerService.institution.id) : null;
    if (storedAssociation) {
      this.userInstitutionPermissions = this.transformUserPermissions(storedAssociation.permissionList);
      this.userInstitutionRole = storedAssociation.roleName;
      // if user is invited status, then enable them
      if (storedAssociation.status === 'INVITED') {
        this.userMgmtService.enableUser(user.id).subscribe(
           error => { console.log(error); }// TODO: handle error
        );
      }
    } else {
      this.customerService.getOrderedAncestorIds().subscribe(
        (ancestralAssociationIds: number[]) => {
          const effectiveRole = ancestralAssociationIds.map(id => this.transformInstitutionIdToRole(user, id))
            .filter(it => it)
            .reduce((prevAssociation, curAssociation) => {
              return (prevAssociation.roleValue < curAssociation.roleValue) ? curAssociation : prevAssociation;
            });
          this.userInstitutionPermissions = this.transformUserPermissions(effectiveRole.permissionList);
          this.userInstitutionRole = effectiveRole.roleName;
        }
      );
    }
  }


  clearRoleAndPermissions(): void {
    console.log('Clear Role & Permissions');
    this.userInstitutionRole = null;
    this.userInstitutionPermissions = null;

  }

  private transformInstitutionIdToRole(user: IUser, institutionId: number): Role {
    const association = user.userAssociations.find(uai => uai.institutionId === institutionId);
    if (association) {
      association.roleValue = StrategiesPermissionsService.ROLE_VALUE_MAP[association.roleName];
    }
    return association;
  }

  private transformUserPermissions(permissions: string[]) {
    const perms = new StrategyPermissions();
    permissions.forEach(permission => {
      switch (permission) {
        case StrategyPermissions.CAN_ADD_STRATEGY:
          perms.canAdd = true;
          break;
        case StrategyPermissions.CAN_VIEW_STRATEGY:
          perms.canView = true;
          break;
        case StrategyPermissions.CAN_EDIT_STRATEGY_BODY:
          perms.canEditBody = true;
          break;
        case StrategyPermissions.CAN_EDIT_STRATEGY_NAME_DATES:
          perms.canEditNameDates = true;
          break;
        case StrategyPermissions.CAN_DOWNLOAD_STRATEGY:
          perms.canDownload = true;
          break;
        case StrategyPermissions.CAN_DELETE_STRATEGY:
          perms.canDelete = true;
          break;
        case StrategyPermissions.CAN_COPY_STRATEGY:
          perms.canCopy = true;
          break;
        case StrategyPermissions.CAN_MANAGE_USERS:
          perms.canManageUsers = true;
          break;
      }
    });
    return perms;
  }
}
